import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { useSessions, useTopics } from "@/hooks/useQueryHooks";
import { supabase } from "@/integrations/supabase/client";
import { devLogger } from "@/lib/dev-logger";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { IconHome, IconMessage, IconCompass, IconWorld, IconSettings, IconPlus, IconLayoutSidebarLeftCollapse, IconSearch, IconLayoutSidebar, IconSmartHome, IconBrandSafari, IconChevronDown, IconUser, IconHelp, IconLogout, IconExternalLink, IconHelpCircle } from "@tabler/icons-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Separator } from "@/components/ui/separator";
import ProfilePopup from "./ProfilePopup";
import SidebarCreditDisplay from "./subscription/SidebarCreditDisplay";
import SidebarHeader from "./sidebar/SidebarHeader";
import NewTopicModal from "./NewTopicModal";
import SidebarCollapsed from "./sidebar/SidebarCollapsed";
import SidebarNewTopicButton from "./sidebar/SidebarNewTopicButton";
import SidebarProfile from "./sidebar/SidebarProfile";
import SidebarTopics from "./sidebar/SidebarTopics";
import SearchModal from "./search/SearchModal";
import { RenameTopicModal } from "./topic/RenameTopicModal";
import { DeleteTopicModal } from "./topic/DeleteTopicModal";
import { useCreateTopic } from "@/hooks/useCreateTopic";
import { useDeleteTopic } from "@/hooks/useDeleteTopic";
interface Session {
  id: string;
  title: string;
  last_activity_at: string;
  total_duration_seconds: number;
}
interface SidebarProps {
  onToggle?: (collapsed: boolean) => void;
  showHeader?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
}
const Sidebar = ({
  onToggle,
  showHeader = false,
  onClose,
  collapsed = false
}: SidebarProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentPath = location.pathname;
  const activeTopicId = currentPath.startsWith('/topic/') ? currentPath.split('/')[2] : undefined;
  const {
    user
  } = useAuth();
  const {
    toast
  } = useToast();
  const queryClient = useQueryClient();
  // Remove internal state and use prop instead
  const isCollapsed = collapsed;
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const [isRenameModalOpen, setIsRenameModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<{
    full_name?: string | null;
    avatar_url?: string | null;
  }>({});
  const {
    createTopic,
    isCreating
  } = useCreateTopic();
  const {
    deleteTopic,
    isDeleting
  } = useDeleteTopic();
  const scrollAreaRef = useRef<HTMLDivElement>(null);

  // Use query hooks for data fetching
  const {
    data: topics = [],
    isLoading: topicsLoading
  } = useTopics();
  const {
    data: sessions = [],
    isLoading: sessionsLoading
  } = useSessions();

  // Fetch user profile
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user) return;
      try {
        const {
          data: profile,
          error
        } = await supabase.from('profiles').select('full_name, avatar_url').eq('id', user.id).maybeSingle();
        if (!error && profile) {
          setUserProfile({
            full_name: profile.full_name,
            avatar_url: profile.avatar_url
          });
        }
      } catch (error) {
        console.error('Error fetching user profile:', error);
      }
    };
    fetchUserProfile();
  }, [user]);

  // Set up scroll event cleanup
  useEffect(() => {
    return () => {
      if (window.scrollTimeout) {
        clearTimeout(window.scrollTimeout);
      }
    };
  }, []); // Setup once when component mounts

  // Set up realtime subscriptions for topic and session updates with query invalidation
  useEffect(() => {
    if (!user) return;
    const topicChannel = supabase.channel('topic-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'topics',
      filter: `user_id=eq.${user.id}`
    }, payload => {
      devLogger.log('Topic change:', payload);
      // Invalidate queries to refetch fresh data
      queryClient.invalidateQueries({
        queryKey: ['topics', user.id]
      });
      if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
        queryClient.invalidateQueries({
          queryKey: ['topic', payload.new?.id]
        });
      }
    }).subscribe();
    const sessionChannel = supabase.channel('session-changes').on('postgres_changes', {
      event: '*',
      schema: 'public',
      table: 'sessions',
      filter: `user_id=eq.${user.id}`
    }, payload => {
      devLogger.log('Session change:', payload);
      // Invalidate session queries for fresh data
      queryClient.invalidateQueries({
        queryKey: ['sessions', user.id]
      });
      if (payload.eventType === 'UPDATE' || payload.eventType === 'INSERT') {
        queryClient.invalidateQueries({
          queryKey: ['session', payload.new?.id]
        });
        // Also invalidate document sessions if document_id is available
        if (payload.new?.document_id) {
          queryClient.invalidateQueries({
            queryKey: ['document-sessions', payload.new.document_id]
          });
        }
      }
    }).subscribe();
    return () => {
      supabase.removeChannel(topicChannel);
      supabase.removeChannel(sessionChannel);
    };
  }, [user, queryClient]);

  // Helper function to format date
  const formatDate = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    if (diffInHours < 24) {
      return 'Today';
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short'
      });
    }
  };

  // Helper function to categorize sessions by date
  const categorizeSessions = (sessions: Session[]) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
    const categorized = {
      today: [] as Session[],
      yesterday: [] as Session[],
      previous30Days: [] as Session[],
      earlier: [] as Session[]
    };
    sessions.forEach(session => {
      const sessionDate = new Date(session.last_activity_at);
      const sessionDateOnly = new Date(sessionDate.getFullYear(), sessionDate.getMonth(), sessionDate.getDate());
      if (sessionDateOnly.getTime() >= today.getTime()) {
        categorized.today.push(session);
      } else if (sessionDateOnly.getTime() >= yesterday.getTime()) {
        categorized.yesterday.push(session);
      } else if (sessionDateOnly.getTime() >= thirtyDaysAgo.getTime()) {
        categorized.previous30Days.push(session);
      } else {
        categorized.earlier.push(session);
      }
    });
    return categorized;
  };
  const categorizedSessions = categorizeSessions(sessions);

  // Filter topics based on search query
  const filteredTopics = topics.filter(topic => topic.title.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleHomeClick = () => {
    navigate("/");
    onClose?.();
  };
  const handleExploreClick = () => {
    navigate("/explore");
    onClose?.();
  };
  const handleTopicClick = (topicId: string) => {
    navigate(`/topic/${topicId}`);
    // Don't close sidebar when clicking topics - preserve sidebar state
  };
  const handleSessionClick = (sessionId: string) => {
    navigate(`/session/${sessionId}`);
    onClose?.();
  };
  const handleRenameTopic = (topicId: string) => {
    setSelectedTopicId(topicId);
    setIsRenameModalOpen(true);
  };
  const handleDeleteTopic = (topicId: string) => {
    setSelectedTopicId(topicId);
    setIsDeleteModalOpen(true);
  };
  const handleRename = async (newTitle: string, newInstructions: string) => {
    if (!selectedTopicId) return;
    try {
      const {
        error
      } = await supabase.from('topics').update({
        title: newTitle,
        instructions: newInstructions
      }).eq('id', selectedTopicId);
      if (error) throw error;
      toast({
        title: "Topic updated",
        description: "Topic has been updated successfully."
      });
      queryClient.invalidateQueries({
        queryKey: ['topics']
      });
      queryClient.invalidateQueries({
        queryKey: ['topic', selectedTopicId]
      });
      setIsRenameModalOpen(false);
      setSelectedTopicId(null);
    } catch (error) {
      console.error('Error updating topic:', error);
      toast({
        title: "Error",
        description: "Failed to update topic. Please try again.",
        variant: "destructive"
      });
    }
  };
  const handleConfirmDelete = async () => {
    if (!selectedTopicId || !user) return;
    const topicToDelete = topics.find(t => t.id === selectedTopicId);
    const topicTitle = topicToDelete?.title;
    try {
      // Use backend API to delete topic (handles corpus + database cleanup)
      await deleteTopic({
        topicId: selectedTopicId,
        topicTitle,
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          setSelectedTopicId(null);

          // Navigate to home if we're currently viewing the deleted topic
          if (activeTopicId === selectedTopicId) {
            navigate('/');
            onClose?.();
          }
        }
      });
    } catch (error) {
      // Error handling is done in useDeleteTopic hook
      console.error('Error deleting topic:', error);
    }
  };
  const toggleSidebar = () => {
    const newCollapsed = !isCollapsed;
    onToggle?.(newCollapsed);
  };
  const handleExpand = () => {
    onToggle?.(false);
  };
  // Remove this useEffect as it's no longer needed
  // useEffect(() => {
  //   onToggle?.(isCollapsed);
  // }, [isCollapsed, onToggle]);
  return <div className={`h-full flex flex-col transition-all duration-700 ease-out ${isCollapsed ? 'w-auto' : 'w-68'}`} style={{
    backgroundColor: '#EBEBEB'
  }}>
      {/* Logo Header with collapse button when opened */}
      {!isCollapsed && <div className="p-4 flex items-center justify-between py-[12px]">
          <img src="/lovable-uploads/9fafa11b-18a8-492b-82d9-633fb47070a5.png" alt="Studibudi" className="h-6 w-auto" />
          <div onClick={toggleSidebar} className="cursor-pointer p-2 rounded-lg">
            <IconLayoutSidebar className="w-6 h-6 text-gray-600" stroke={1.6} />
          </div>
        </div>}
      
      {showHeader && <SidebarHeader isCollapsed={isCollapsed} onToggle={toggleSidebar} onHomeClick={handleHomeClick} showCloseButton={!!onClose} onClose={onClose} />}

    {!isCollapsed ? <div className="flex-1 flex flex-col min-h-0">
          
          {/* Main Navigation */}
          <div className="space-y-1 px-4 mb-3 pb-3 border-b border-gray-300 flex-shrink-0">
            <div onClick={() => {
          navigate('/');
          onClose?.();
        }} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${currentPath === '/' ? "bg-white" : "hover:bg-white/50"}`}>
              <IconSmartHome className="w-5 h-5 text-foreground/70" stroke={1.6} />
              <span className="text-sm font-normal text-foreground">
                Home
              </span>
            </div>

            <div onClick={() => setIsSearchModalOpen(true)} className="flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer hover:bg-white/50">
              <IconSearch className="w-5 h-5 text-foreground/70" stroke={1.6} />
              <span className="text-sm font-normal text-foreground">
                Search
              </span>
            </div>

            <div onClick={() => {
          navigate('/explore');
          onClose?.();
        }} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200 cursor-pointer ${currentPath === '/explore' ? "bg-white" : "hover:bg-white/50"}`}>
              <IconBrandSafari className="w-5 h-5 text-foreground/70" stroke={1.6} />
              <span className="text-sm font-normal text-foreground">
                Explore
              </span>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col min-h-0 overflow-hidden">

            {/* New Topic Button - hide when searching */}
            {/* {!searchQuery && <div className="flex-shrink-0"><SidebarNewTopicButton onClose={onClose} /></div>} */}

            <div className="flex-1 min-h-0 overflow-hidden">
              <div ref={scrollAreaRef} className={`h-full px-3 overflow-y-auto ${isScrolling ? 'scrollbar-thin-light' : 'scrollbar-hide'}`} onScroll={() => {
            setIsScrolling(true);
            if (window.scrollTimeout) {
              clearTimeout(window.scrollTimeout);
            }
            window.scrollTimeout = setTimeout(() => setIsScrolling(false), 1000);
          }}>
                <SidebarTopics topics={filteredTopics} searchQuery={searchQuery} onTopicClick={handleTopicClick} onTopicRename={handleRenameTopic} onTopicDelete={handleDeleteTopic} activeTopicId={activeTopicId} />
              </div>
            </div>
          </div>

          {/* Fixed Footer Section - User Avatar and Credits */}
          <div className="bg-[#EBEBEB] flex-shrink-0 mt-auto px-[12px] py-[12px] ">
            {/* Credits Section */}
            <SidebarCreditDisplay className="mx-[24px] rounded-bl-[0px] rounded-br-[0px]" />

            {/* User Profile */}
            <Popover>
              <PopoverTrigger asChild>
                <div className="flex items-center gap-3 cursor-pointer bg-white hover:bg-gray-50 transition-colors px-[8px] py-[8px] rounded-2xl w-full border border-gray-300">
                  <Avatar className="h-6 w-6">
                    <AvatarImage src={userProfile.avatar_url || undefined} alt={userProfile.full_name || "User"} />
                    <AvatarFallback className="bg-gray-300 text-gray-700 text-sm">
                      {userProfile.full_name ? userProfile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {userProfile.full_name || user?.email?.split('@')[0] || "User"}
                    </p>
                  </div>
                  <IconChevronDown className="w-4 h-4 text-foreground/70" stroke={1.6} />
                </div>
              </PopoverTrigger>
              <PopoverContent 
                align="start" 
                side="top"
                className="w-full bg-white border border-gray-200 shadow-lg rounded-2xl z-[100]"
                sideOffset={8}
                style={{ width: 'calc(286px - 24px)' }}
              >
                 <div className="py-1 px-3 space-y-1">
                  {/* User Info Header */}
                  <div className="flex items-center gap-3 py-2">
                    <Avatar className="w-12 h-12 rounded-2xl">
                      <AvatarImage src={userProfile.avatar_url || undefined} alt={userProfile.full_name || "User"} />
                      <AvatarFallback className="bg-[#EDEDEB] text-foreground text-sm font-medium border border-[#DADAD9] rounded-2xl">
                        {userProfile.full_name ? userProfile.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2) : user?.email?.charAt(0).toUpperCase() || "U"}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-foreground truncate">
                        {userProfile.full_name || user?.email?.split('@')[0] || "User"}
                      </p>
                      <p className="text-sm text-muted-foreground truncate">
                        {user?.email}
                      </p>
                    </div>
                  </div>

                  {/* Credit Details Section */}
                  <div className="px-3 py-2 space-y-1.5 border rounded-2xl" style={{ backgroundColor: '#F7F7F7', borderColor: '#E9E9E9' }}>
                    {/* Free/Upgrade Row */}
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-foreground">Free</span>
                      <button className="px-4 py-1.5 bg-foreground text-primary-foreground text-sm font-medium rounded-full hover:bg-foreground/90 transition-colors">
                        Upgrade
                      </button>
                    </div>

                    {/* Dashed Separator */}
                    <div className="border-t border-dashed border-gray-300" />

                    {/* Credits Row */}
                    <button className="w-full flex items-center justify-between py-2 hover:bg-gray-50 rounded-lg transition-colors">
                      <div className="flex items-center gap-2">
                        <img src="/lovable-uploads/3dc23cd9-8051-4e9c-b14f-9d3ee05ae883.png" alt="Credits" className="h-5 w-auto" />
                        <span className="text-sm font-medium text-foreground">Credits</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-foreground">1,300</span>
                        <svg className="w-4 h-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                      </div>
                    </button>
                  </div>

                  {/* Menu Items */}
                  <ProfilePopup>
                    <button className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left">
                      <IconUser className="w-5 h-5 text-foreground/70" stroke={1.6} />
                      <span className="text-sm font-medium text-foreground">Account</span>
                    </button>
                  </ProfilePopup>

                  <Separator className="my-2" />

                  <button
                    onClick={() => window.open('https://studibudi.ai/', '_blank')}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <IconHome className="w-5 h-5 text-foreground/70" stroke={1.6} />
                      <span className="text-sm font-medium text-foreground">Homepage</span>
                    </div>
                    <IconExternalLink className="w-4 h-4 text-muted-foreground" stroke={1.6} />
                  </button>

                  <button
                    onClick={() => window.open('mailto:support@studibudi.com', '_blank')}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <IconHelpCircle className="w-5 h-5 text-foreground/70" stroke={1.6} />
                      <span className="text-sm font-medium text-foreground">Contact us</span>
                    </div>
                    <IconExternalLink className="w-4 h-4 text-muted-foreground" stroke={1.6} />
                  </button>

                  <Separator className="my-2" />

                  <button
                    onClick={async () => {
                      await supabase.auth.signOut();
                      navigate('/login');
                    }}
                    className="w-full flex items-center gap-3 px-3 py-2 rounded-2xl hover:bg-gray-50 transition-colors text-left"
                  >
                    <IconLogout className="w-5 h-5 text-red-500" stroke={1.6} />
                    <span className="text-sm font-medium text-red-500">Sign out</span>
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div> : <div className="flex-1 flex flex-col"><SidebarCollapsed onExpand={handleExpand} onHomeClick={handleHomeClick} onSearchClick={() => setIsSearchModalOpen(true)} onExploreClick={handleExploreClick} onNewTopicClick={async () => {
        const result = await createTopic("New Topic", 5, [], { onClose: () => {} });
        if (result?.id) {
          navigate(`/topic/${result.id}`);
          onClose?.();
        }
      }} /></div>}
      
            <SearchModal isOpen={isSearchModalOpen} onOpenChange={setIsSearchModalOpen} onClose={onClose} />
            
            {selectedTopicId && <>
                <RenameTopicModal open={isRenameModalOpen} onOpenChange={setIsRenameModalOpen} currentTitle={topics.find(t => t.id === selectedTopicId)?.title || ''} currentInstructions={topics.find(t => t.id === selectedTopicId)?.instructions || ''} onConfirm={handleRename} />
                
                <DeleteTopicModal open={isDeleteModalOpen} onOpenChange={setIsDeleteModalOpen} topicTitle={topics.find(t => t.id === selectedTopicId)?.title || ''} onConfirm={handleConfirmDelete} isLoading={isDeleting} />
              </>}
    </div>;
};
export default Sidebar;